import { Suspense } from 'react'
import {
  CoachesBillingDashboardResource,
  CoachesBillingResource
} from 'coachesBillingResource.types'
import { Loader } from 'components/Loader'
import { PrivacyModeWarning } from 'components/PrivacyModeWarning/PrivacyModeWarning'
import { PrivatePageLayout } from 'components/PrivatePageLayout'
import { PrivateRoute } from 'components/PrivateRoute'
import { SnackBar } from 'components/SnackBar'
import { usePrivacyMode } from 'hooks/usePrivacyMode'
import { KnowledgeResource } from 'knowledgeResource.types'
import { LocationPaths } from 'location.types'
import { NutritionistOrganizationResource } from 'nutritionistOrganizationResource.types'
import { Navigate, Route, Routes } from 'react-router'
import { REGISTRATION_GATE_PATHS } from 'screens/NutritionistOnboarding/getRegistrationGateRedirect'
import { OnboardingRoute } from 'screens/NutritionistOnboarding/OnboardingRoute'
import { DeleteNoteDialog } from 'screens/UserDetails/Events/DeleteNoteDialog'
import { ShippingResource } from 'shippingResource.types'
import { UserAuthorizationResource } from 'userAuthorizationResource.types'
import { lazyWithReload } from 'utility/lazyWithReload'
import { FeatureGrant } from 'types'
import { NotFound } from './components/NotFound'
import { OpsPageLayout } from './components/OpsPageLayout'
import { SaveEventDialog } from './screens/UserDetails/SaveEventDialog'
import { UserResource } from './userResource.types'

const UserDetails = lazyWithReload(() =>
  import('./screens/UserDetails').then((module) => ({ default: module.UserDetails }))
)
const Login = lazyWithReload(() =>
  import('./screens/Login').then((module) => ({ default: module.Login }))
)

const AmazonOrders = lazyWithReload(() =>
  import('./screens/AmazonOrders').then((module) => ({ default: module.AmazonOrders }))
)

const Orders = lazyWithReload(() =>
  import('./screens/Orders').then((module) => ({ default: module.Orders }))
)

const Shipments = lazyWithReload(() =>
  import('./screens/Shipments').then((module) => ({ default: module.Shipments }))
)

const InvoiceOrders = lazyWithReload(() =>
  import('./screens/InvoiceOrders').then((module) => ({ default: module.InvoiceOrders }))
)

const Files = lazyWithReload(() =>
  import('./screens/Files').then((module) => ({ default: module.Files }))
)

const Chatbot = lazyWithReload(() =>
  import('./screens/Chatbot').then((module) => ({ default: module.Chatbot }))
)

const History = lazyWithReload(() =>
  import('./screens/History').then((module) => ({ default: module.History }))
)

const Questionnaire = lazyWithReload(() =>
  import('./screens/Questionnaire').then((module) => ({ default: module.Questionnaire }))
)

const AdminPanel = lazyWithReload(() =>
  import('./screens/AdminPanel').then((module) => ({ default: module.AdminPanel }))
)

const ResetPasswordRequest = lazyWithReload(() =>
  import('./screens/ResetPasswordRequest').then((module) => ({
    default: module.ResetPasswordRequest
  }))
)

const Financials = lazyWithReload(() =>
  import('./screens/Financials').then((module) => ({ default: module.Financials }))
)

const ReplacementRequests = lazyWithReload(() =>
  import('./screens/ReplacementRequests').then((module) => ({
    default: module.ReplacementRequests
  }))
)

const UserShipments = lazyWithReload(() =>
  import('./screens/UserShipments').then((module) => ({ default: module.UserShipments }))
)

const Surveys = lazyWithReload(() =>
  import('./screens/Surveys').then((module) => ({
    default: module.Surveys
  }))
)

const AllCompletedNotifications = lazyWithReload(() =>
  import('./screens/AdminPanel/Notifications/CompletedNotifications/CompletedNotifications').then(
    (module) => ({
      default: module.CompletedNotifications
    })
  )
)

const Insights = lazyWithReload(() =>
  import('./screens/Insights').then((module) => ({
    default: module.Insights
  }))
)

const KnowledgeQuestions = lazyWithReload(() =>
  import('./screens/Knowledge').then((module) => ({ default: module.Questions }))
)

const KnowledgeAnswers = lazyWithReload(() =>
  import('./screens/Knowledge').then((module) => ({ default: module.Answers }))
)

const KnowledgeSources = lazyWithReload(() =>
  import('./screens/Knowledge').then((module) => ({ default: module.Sources }))
)

const KnowledgeTags = lazyWithReload(() =>
  import('./screens/Knowledge').then((module) => ({ default: module.Tags }))
)

const SupportTickets = lazyWithReload(() =>
  import('./screens/SupportTickets').then((module) => ({ default: module.SupportTickets }))
)

const ChatbotMessageReports = lazyWithReload(() =>
  import('./screens/ChatbotMessageReports').then((module) => ({
    default: module.ChatbotMessageReports
  }))
)

const MobileAppPresentationMode = lazyWithReload(() =>
  import('./screens/MobileAppPresentationMode').then((module) => ({
    default: module.MobileAppPresentationMode
  }))
)

const Chat = lazyWithReload(() =>
  import('./screens/Chat').then((module) => ({ default: module.Chat }))
)

const NutritionistsOrganization = lazyWithReload(() =>
  import('./screens/NutritionistsOrganization').then((module) => ({
    default: module.NutritionistsOrganization
  }))
)

const CoachesBillingDashboardUsers = lazyWithReload(() =>
  import('./screens/CoachesBilling/Dashboard').then((module) => ({
    default: module.InvoiceUsers
  }))
)

const CoachesBillingDashboardTotals = lazyWithReload(() =>
  import('./screens/CoachesBilling/Dashboard').then((module) => ({
    default: module.InvoiceItemTotals
  }))
)

const CoachesBillingInvoiceDetails = lazyWithReload(() =>
  import('./screens/CoachesBilling/InvoiceDetails').then((module) => ({
    default: module.CoachesBillingInvoiceDetails
  }))
)

const UserAuthorizationPolicies = lazyWithReload(() =>
  import('./screens/UserAuthorization').then((module) => ({
    default: module.Policies
  }))
)

const UserAppointments = lazyWithReload(() =>
  import('./screens/UserAppointments').then((module) => ({
    default: module.UserAppointments
  }))
)

const UserAuthorizationRole = lazyWithReload(() =>
  import('./screens/UserAuthorization').then((module) => ({
    default: module.Role
  }))
)

const OrganizationMembers = lazyWithReload(() =>
  import('./screens/OrganizationMembers').then((module) => ({
    default: module.OrganizationMembers
  }))
)

const AccountRequests = lazyWithReload(() =>
  import('./screens/AccountRequests').then((module) => ({ default: module.AccountRequests }))
)

const AdminPanelLayout = lazyWithReload(() =>
  import('./screens/AdminPanel').then((module) => ({ default: module.AdminPanelLayout }))
)

const DashboardLayout = lazyWithReload(() =>
  import('./screens/CoachesBilling/Dashboard').then((module) => ({
    default: module.DashboardLayout
  }))
)

const FormulasLayout = lazyWithReload(() =>
  import('./screens/Formulas/FormulasLayout').then((module) => ({ default: module.FormulasLayout }))
)

const MembersManagement = lazyWithReload(() =>
  import('./screens/MembersManagement').then((module) => ({ default: module.MembersManagement }))
)

const MobileAppPresentationModeLayout = lazyWithReload(() =>
  import('./screens/MobileAppPresentationMode/MobileAppPresentationModeLayout').then((module) => ({
    default: module.MobileAppPresentationModeLayout
  }))
)

const ProfileSetupScreen = lazyWithReload(() =>
  import('./screens/NutritionistOnboarding/ProfileSetupScreen').then((module) => ({
    default: module.ProfileSetupScreen
  }))
)

const AvailabilitySnapshots = lazyWithReload(() =>
  import('./screens/NutritionistsOrganization/AvailabilitySnapshots/AvailabilitySnapshots').then(
    (module) => ({ default: module.AvailabilitySnapshots })
  )
)

const NutritionistsGrid = lazyWithReload(() =>
  import('./screens/NutritionistsOrganization/NutritionistsGrid').then((module) => ({
    default: module.NutritionistsGrid
  }))
)

const UserAuthorizationLayout = lazyWithReload(() =>
  import('./screens/UserAuthorization/UserAuthorizationLayout').then((module) => ({
    default: module.UserAuthorizationLayout
  }))
)

const WebMembersPresentationMode = lazyWithReload(() =>
  import('./screens/WebMembersPresentationMode').then((module) => ({
    default: module.WebMembersPresentationMode
  }))
)

const WebMembersPresentationModeLayout = lazyWithReload(() =>
  import('./screens/WebMembersPresentationMode').then((module) => ({
    default: module.WebMembersPresentationModeLayout
  }))
)

const BillingDashboard = lazyWithReload(() =>
  import('./screens/BillingDashboard/BillingDashboard').then((module) => ({
    default: module.BillingDashboard
  }))
)

const CoachCredentialsScreen = lazyWithReload(() =>
  import('./screens/CoachCredentials').then((module) => ({
    default: module.CoachCredentialsScreen
  }))
)

const CoachCredentialsReviewScreen = lazyWithReload(() =>
  import('./screens/CoachCredentialsReview').then((module) => ({
    default: module.CoachCredentialsReviewScreen
  }))
)

const KnowledgeLayout = lazyWithReload(() =>
  import('./screens/Knowledge').then((module) => ({ default: module.KnowledgeLayout }))
)

const NutritionistHome = lazyWithReload(() =>
  import('./screens/NutritionistHome/NutritionistHome').then((module) => ({
    default: module.NutritionistHome
  }))
)

export const App = () => {
  usePrivacyMode()

  const userDetailsPrivate = (
    <PrivateRoute feature={FeatureGrant.Home}>
      <UserDetails />
    </PrivateRoute>
  )

  const insightsPrivate = (
    <PrivateRoute feature={FeatureGrant.Insights}>
      <Insights />
    </PrivateRoute>
  )

  const filesPrivate = (
    <PrivateRoute feature={FeatureGrant.Files}>
      <Files />
    </PrivateRoute>
  )

  const chatbotPrivate = (
    <PrivateRoute feature={FeatureGrant.ChatbotUserHistory}>
      <Chatbot />
    </PrivateRoute>
  )

  const historyPrivate = (
    <PrivateRoute feature={FeatureGrant.History}>
      <History />
    </PrivateRoute>
  )

  const questionnairePrivate = (
    <PrivateRoute feature={FeatureGrant.Questionnaire}>
      <Questionnaire />
    </PrivateRoute>
  )

  const adminViewPrivate = (
    <PrivateRoute feature={FeatureGrant.AdminPanelMembers}>
      <AdminPanel />
    </PrivateRoute>
  )

  const amazonOrdersPrivate = (
    <PrivateRoute feature={FeatureGrant.ShipmentsManagement}>
      <AmazonOrders />
    </PrivateRoute>
  )

  const ordersPrivate = (
    <PrivateRoute feature={FeatureGrant.ShipmentsManagement}>
      <Orders />
    </PrivateRoute>
  )

  const shipmentsPrivate = (
    <PrivateRoute feature={FeatureGrant.ShipmentsManagement}>
      <Shipments />
    </PrivateRoute>
  )

  const invoiceOrdersPrivate = (
    <PrivateRoute feature={FeatureGrant.ShipmentsManagement}>
      <InvoiceOrders />
    </PrivateRoute>
  )

  const financialsPrivate = (
    <PrivateRoute feature={FeatureGrant.Financials}>
      <Financials />
    </PrivateRoute>
  )

  const replacementRequestsPrivate = (
    <PrivateRoute feature={FeatureGrant.SensorsReplacementRequests}>
      <ReplacementRequests />
    </PrivateRoute>
  )

  const userShipmentsPrivate = (
    <PrivateRoute feature={FeatureGrant.ShipmentsViewUser}>
      <UserShipments />
    </PrivateRoute>
  )

  const surveysPrivate = (
    <PrivateRoute feature={FeatureGrant.SurveysManagement}>
      <Surveys />
    </PrivateRoute>
  )

  const allCompletedNotificationsPrivate = (
    <PrivateRoute feature={FeatureGrant.AdminPanelMembers}>
      <AllCompletedNotifications />
    </PrivateRoute>
  )

  const chatPrivate = (
    <PrivateRoute feature={FeatureGrant.Chat}>
      <Chat />
    </PrivateRoute>
  )

  const knowledgeQuestionsPrivate = (
    <PrivateRoute feature={FeatureGrant.ChatbotViewKnowledgeBase}>
      <KnowledgeQuestions />
    </PrivateRoute>
  )

  const knowledgeAnswersPrivate = (
    <PrivateRoute feature={FeatureGrant.ChatbotViewKnowledgeBase}>
      <KnowledgeAnswers />
    </PrivateRoute>
  )

  const knowledgeSourcesPrivate = (
    <PrivateRoute feature={FeatureGrant.ChatbotViewKnowledgeBase}>
      <KnowledgeSources />
    </PrivateRoute>
  )

  const knowledgeTagsPrivate = (
    <PrivateRoute feature={FeatureGrant.ChatbotViewKnowledgeBase}>
      <KnowledgeTags />
    </PrivateRoute>
  )

  const supportTicketsPrivate = (
    <PrivateRoute feature={FeatureGrant.SupportTickets}>
      <SupportTickets />
    </PrivateRoute>
  )

  const chatbotMessageReportsPrivate = (
    <PrivateRoute feature={FeatureGrant.ChatbotMessageReports}>
      <ChatbotMessageReports />
    </PrivateRoute>
  )

  const accountRequestsPrivate = (
    <PrivateRoute feature={FeatureGrant.SupportAccountRequests}>
      <AccountRequests />
    </PrivateRoute>
  )

  const billingDashboardPrivate = (
    <PrivateRoute feature={FeatureGrant.BillingDashboard}>
      <BillingDashboard />
    </PrivateRoute>
  )

  const userAppointmentsPrivate = (
    <PrivateRoute feature={FeatureGrant.ViewAppointments}>
      <UserAppointments />
    </PrivateRoute>
  )

  const mobileAppPresentationMode = (
    <PrivateRoute feature={FeatureGrant.HomeMobileAppPresentationMode}>
      <MobileAppPresentationMode />
    </PrivateRoute>
  )

  const webMembersPresentationMode = (
    <PrivateRoute feature={FeatureGrant.HomeMobileAppPresentationMode}>
      <WebMembersPresentationMode />
    </PrivateRoute>
  )

  const nutritionistHomePrivate = (
    <PrivateRoute feature={FeatureGrant.NutritionistHome}>
      <NutritionistHome />
    </PrivateRoute>
  )

  const coachCredentialsPrivate = (
    <PrivateRoute feature={FeatureGrant.NutritionistHome}>
      <CoachCredentialsScreen />
    </PrivateRoute>
  )

  const coachCredentialsReviewPrivate = (
    <PrivateRoute feature={FeatureGrant.ViewHealthTeamStructure}>
      <CoachCredentialsReviewScreen />
    </PrivateRoute>
  )

  const membersManagementPrivate = (
    <PrivateRoute feature={FeatureGrant.MembersManagement}>
      <MembersManagement />
    </PrivateRoute>
  )

  const nutritionistsGridPrivate = (
    <PrivateRoute feature={FeatureGrant.ViewHealthTeamStructure}>
      <NutritionistsGrid />
    </PrivateRoute>
  )

  const coachesBillingDashboardUsersPrivate = (
    <PrivateRoute feature={FeatureGrant.CoachesBillingDashboard}>
      <CoachesBillingDashboardUsers />
    </PrivateRoute>
  )

  const coachesBillingDashboardTotalsPrivate = (
    <PrivateRoute feature={FeatureGrant.CoachesBillingDashboard}>
      <CoachesBillingDashboardTotals />
    </PrivateRoute>
  )

  const coachesBillingInvoiceDetailsPrivate = (
    <PrivateRoute feature={FeatureGrant.CoachesBillingDetails}>
      <CoachesBillingInvoiceDetails />
    </PrivateRoute>
  )

  const coachAvailabilityPrivate = (
    <PrivateRoute feature={FeatureGrant.CoachAvailability}>
      <AvailabilitySnapshots />
    </PrivateRoute>
  )

  const userAuthorizationPoliciesPrivate = (
    <PrivateRoute feature={FeatureGrant.AuthorizationManagement}>
      <UserAuthorizationPolicies />
    </PrivateRoute>
  )

  const userAuthorizationRolePrivate = (
    <PrivateRoute feature={FeatureGrant.AuthorizationManagement}>
      <UserAuthorizationRole />
    </PrivateRoute>
  )

  const organizationMembersPrivate = (
    <PrivateRoute feature={FeatureGrant.OrganizationMembers}>
      <OrganizationMembers />
    </PrivateRoute>
  )

  const formulasPrivate = (
    <PrivateRoute feature={FeatureGrant.Formulas}>
      <FormulasLayout />
    </PrivateRoute>
  )

  return (
    <>
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<Navigate to={LocationPaths.AdminPanel} replace />} />
          <Route path="/reset-password-request" element={<ResetPasswordRequest />} />
          <Route path="/login" element={<Login />} />
          <Route
            path={REGISTRATION_GATE_PATHS.profile}
            element={
              <OnboardingRoute>
                <ProfileSetupScreen />
              </OnboardingRoute>
            }
          />

          <Route path="/" element={<PrivatePageLayout />}>
            <Route path={LocationPaths.AdminPanel} element={<AdminPanelLayout />}>
              <Route path={LocationPaths.AdminPanel} element={adminViewPrivate} />
              <Route
                path={LocationPaths.AllCompletedNotifications}
                element={allCompletedNotificationsPrivate}
              />
            </Route>
            <Route path={LocationPaths.ReplacementRequests} element={replacementRequestsPrivate} />
            <Route path={LocationPaths.Surveys} element={surveysPrivate} />
            <Route path={LocationPaths.Financials} element={financialsPrivate} />
            <Route path={LocationPaths.SupportTickets} element={supportTicketsPrivate} />
            <Route
              path={LocationPaths.ChatbotMessageReports}
              element={chatbotMessageReportsPrivate}
            />
            <Route path={LocationPaths.AccountRequests} element={accountRequestsPrivate} />
            <Route path={LocationPaths.Knowledge} element={<KnowledgeLayout />}>
              <Route path="" element={<Navigate to={KnowledgeResource.Questions} replace />} />
              <Route path={KnowledgeResource.Questions} element={knowledgeQuestionsPrivate} />
              <Route path={KnowledgeResource.Answers} element={knowledgeAnswersPrivate} />
              <Route path={KnowledgeResource.Sources} element={knowledgeSourcesPrivate} />
              <Route path={KnowledgeResource.Tags} element={knowledgeTagsPrivate} />
            </Route>
            <Route path={LocationPaths.NutritionistHome} element={nutritionistHomePrivate} />
            <Route path={LocationPaths.CoachCredentials} element={coachCredentialsPrivate} />
            <Route
              path={`${LocationPaths.CoachCredentialsReview}/:userId`}
              element={coachCredentialsReviewPrivate}
            />
            <Route path={LocationPaths.MembersManagement} element={membersManagementPrivate} />
            <Route
              path={LocationPaths.NutritionistsOrganization}
              element={<NutritionistsOrganization />}
            >
              <Route
                path=""
                element={<Navigate to={NutritionistOrganizationResource.Profiles} replace />}
              />
              <Route
                path={NutritionistOrganizationResource.Profiles}
                element={nutritionistsGridPrivate}
              />
              <Route
                path={NutritionistOrganizationResource.AvailabilitySnapshots}
                element={coachAvailabilityPrivate}
              />
              <Route
                path={NutritionistOrganizationResource.AvailabilitySnapshotView}
                element={coachAvailabilityPrivate}
              />
            </Route>
            <Route path={LocationPaths.Coaches}>
              <Route path={CoachesBillingResource.BillingDashboard} element={<DashboardLayout />}>
                <Route
                  path=""
                  element={<Navigate to={CoachesBillingDashboardResource.Users} replace />}
                />
                <Route
                  path={CoachesBillingDashboardResource.Users}
                  element={coachesBillingDashboardUsersPrivate}
                />
                <Route
                  path={CoachesBillingDashboardResource.Totals}
                  element={coachesBillingDashboardTotalsPrivate}
                />
              </Route>
              <Route
                path={CoachesBillingResource.InvoiceItems}
                element={coachesBillingInvoiceDetailsPrivate}
              />
            </Route>
            <Route path={LocationPaths.OrganizationMembers} element={organizationMembersPrivate} />
          </Route>

          <Route path={`${LocationPaths.Users}/:id`} element={<PrivatePageLayout />}>
            <Route path="" element={userDetailsPrivate} />
            <Route path={UserResource.History} element={historyPrivate} />
            <Route path={UserResource.Questionnaire} element={questionnairePrivate} />
            <Route path={UserResource.Files} element={filesPrivate} />
            <Route path={UserResource.Chatbot} element={chatbotPrivate} />
            <Route path={UserResource.Shipments} element={userShipmentsPrivate} />
            <Route path={UserResource.Insights} element={insightsPrivate} />
            <Route path={UserResource.Chat} element={chatPrivate} />
            <Route path={UserResource.BillingDashboard} element={billingDashboardPrivate} />
            <Route path={UserResource.Appointments} element={userAppointmentsPrivate} />
            <Route
              path={`${UserResource.Appointments}/:appointmentId`}
              element={userAppointmentsPrivate}
            />
            <Route path={UserResource.Authorization} element={<UserAuthorizationLayout />}>
              <Route
                path=""
                element={<Navigate to={UserAuthorizationResource.Policies} replace />}
              />
              <Route
                path={UserAuthorizationResource.Policies}
                element={userAuthorizationPoliciesPrivate}
              />
              <Route path={UserAuthorizationResource.Role} element={userAuthorizationRolePrivate} />
            </Route>
            <Route path={UserResource.Formulas} element={formulasPrivate} />
          </Route>

          <Route path="/" element={<OpsPageLayout />}>
            <Route path={ShippingResource.Orders} element={ordersPrivate} />
            <Route path={ShippingResource.AmazonOrders} element={amazonOrdersPrivate} />
            <Route path={ShippingResource.Shipments} element={shipmentsPrivate} />
            <Route
              path={`${ShippingResource.Orders}/:id/${ShippingResource.Shipments}`}
              element={shipmentsPrivate}
            />
            <Route path={ShippingResource.InvoiceOrders} element={invoiceOrdersPrivate} />
            <Route path={ShippingResource.Invoices} element={invoiceOrdersPrivate} />
          </Route>

          <Route
            path={`${LocationPaths.Presentation}`}
            element={<MobileAppPresentationModeLayout />}
          >
            <Route path="" element={mobileAppPresentationMode} />
          </Route>
          <Route
            path={`${LocationPaths.WebMembersPresentation}`}
            element={<WebMembersPresentationModeLayout />}
          >
            <Route path="" element={webMembersPresentationMode} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <SnackBar />
      <SaveEventDialog />
      <DeleteNoteDialog />
      <PrivacyModeWarning />
    </>
  )
}
